/**
 * SLA Monitoring Client for WebGameCollection
 * 
 * This client automatically tracks errors, performance metrics, and page views
 * from frontend game applications and sends them to CloudWatch via API Gateway
 * for SLA monitoring and alerting.
 * 
 * Features:
 * - Automatic error tracking (unhandled errors and promise rejections)
 * - Automatic page load performance tracking
 * - Automatic environment detection
 * - Batching for efficient network usage (configurable)
 * - Session tracking
 * - CloudWatch alarms for production SLA violations
 * 
 * Usage:
 * ```typescript
 * import { initializeAnalytics } from '@mental-mint/shared-utils';
 * 
 * // Initialize analytics - SLA monitoring is automatically included
 * initializeAnalytics(GameId.TileClicker);
 * ```
 * 
 * Manual tracking:
 * ```typescript
 * import { trackError, trackPerformance } from '@mental-mint/shared-utils';
 * 
 * try {
 *   // your code
 * } catch (error) {
 *   trackError(error.message, error.stack);
 * }
 * 
 * const startTime = performance.now();
 * // ... operation ...
 * trackPerformance(performance.now() - startTime);
 * ```
 */

import { ENVIRONMENTS, MONITORING_EVENT_TYPES } from '../Analytics/AnalyticsConstants';
import { getEnvironment } from '../Analytics/EnvironmentUtils';
import { logger } from '@mental-mint/shared-utils';

/**
 * CloudWatch Monitoring API Endpoint
 * 
 * ⚠️ IMPORTANT: Update this after deploying the monitoring stack!
 * 
 * To get the endpoint:
 * 1. Deploy: cd packages/monitoring && npm run deploy
 * 2. Copy the "ApiEndpoint" from CDK output
 * 3. Paste it here (it will look like: https://xxxxxxxxxx.execute-api.us-east-2.amazonaws.com/v1/metrics)
 * 
 * If this is empty, monitoring will be automatically disabled.
 */
const MONITORING_API_ENDPOINT = 'https://uqtl5h2pah.execute-api.us-east-2.amazonaws.com/v1/metrics';

export type Environment = typeof ENVIRONMENTS.PRODUCTION | typeof ENVIRONMENTS.DEVELOPMENT;
export type MonitoringEventType = typeof MONITORING_EVENT_TYPES[keyof typeof MONITORING_EVENT_TYPES];

export interface MonitoringConfig {
  /** Page identifier, often a game name (e.g., 'tile-clicker', 'witocracy') - use GAME_IDS constant */
  pageName: string;
  /** Enable/disable monitoring (useful for testing). Defaults to true if API endpoint is configured */
  enabled?: boolean;
  /** Number of events to batch before sending (default: 10) */
  batchSize?: number;
  /** Time in ms to wait before flushing batch (default: 5000) */
  flushInterval?: number;
}

export interface ErrorInfo {
  /** Error message */
  message: string;
  /** Unique error identifier from backend (UUID) for tracking and debugging */
  errorId?: string;
  /** Stack trace */
  stack?: string;
}

export interface MonitoringEvent {
  /** Type of monitoring event */
  eventType: MonitoringEventType;
  /** Game identifier */
  game: string;
  /** Environment (production or development) */
  environment: Environment;
  /** Unix timestamp in milliseconds */
  timestamp: number;
  /** Error information (for error events) */
  errorInfo?: ErrorInfo;
  /** Page load time in milliseconds (for performance events) */
  pageLoadLatency?: number;
  /** Current page URL */
  url?: string;
  /** Browser user agent string */
  userAgent?: string;
  /** Session identifier for correlating events */
  sessionId?: string;
}

export class SLAMonitoringClient {
  private config: Required<MonitoringConfig>;
  private eventQueue: MonitoringEvent[] = [];
  private flushTimer: number | null = null;
  private sessionId: string;
  private apiEndpoint: string;
  private environment: Environment;

  constructor(config: MonitoringConfig) {
    // Automatically determine environment using Analytics helper
    this.environment = getEnvironment() as Environment;
    
    // Get API endpoint from constant (set via env var)
    this.apiEndpoint = MONITORING_API_ENDPOINT;
    
    // Apply defaults
    this.config = {
      enabled: !!this.apiEndpoint, // Auto-enable if endpoint is configured
      batchSize: 10, // Send batch after 10 events
      flushInterval: 5000, // Send batch after 5 seconds
      ...config,
    };

    // Generate unique session ID for correlating events
    this.sessionId = this.generateSessionId();

    if (this.config.enabled && this.apiEndpoint) {
      logger.log(`[Monitoring] Initialized for ${this.config.pageName} (${this.environment})`);
      this.initializeMonitoring();
    } else {
      logger.log(`[Monitoring] Disabled for ${this.config.pageName}${!this.apiEndpoint ? ' - no API endpoint configured' : ''}`);
    }
  }

  /**
   * Generate a unique session identifier
   * Format: timestamp-randomString
   */
  private generateSessionId(): string {
    return `${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Initialize automatic monitoring
   * - Page load performance tracking
   * - Unhandled error tracking
   * - Automatic flush on page unload
   */
  private initializeMonitoring(): void {
    // PERFORMANCE TRACKING
    // Wait for page load, then measure performance
    if (typeof window !== 'undefined' && window.performance) {
      window.addEventListener('load', () => {
        setTimeout(() => {
          const perfData = window.performance.timing;
          const pageLoadTime = perfData.loadEventEnd - perfData.navigationStart;
          
          if (pageLoadTime > 0) {
            logger.log(`[Monitoring] Page load: ${pageLoadTime}ms`);
            this.trackPerformance(pageLoadTime);
          }
        }, 0);
      });
    }

    // ERROR TRACKING
    // Capture all unhandled errors and promise rejections
    if (typeof window !== 'undefined') {
      window.addEventListener('error', (event) => {
        console.error('[Monitoring] Caught unhandled error:', event.message);
        this.trackError(
          event.message || 'Unknown error',
          event.error?.stack || new Error().stack
        );
      });

      window.addEventListener('unhandledrejection', (event) => {
        console.error('[Monitoring] Caught unhandled promise rejection:', event.reason);
        this.trackError(
          `Unhandled Promise Rejection: ${event.reason}`,
          event.reason?.stack || new Error().stack
        );
      });
    }

    // FLUSH ON PAGE UNLOAD
    // Ensure all queued events are sent before user leaves
    if (typeof window !== 'undefined') {
      window.addEventListener('beforeunload', () => {
        this.flush(true);
      });
    }
  }

  /**
   * Generate a unique error ID in format: errId_YYYYMMDD_<uuid-half>
   * Falls back to timestamp-based ID if crypto.randomUUID is unavailable
   */
  private generateErrorId(): string {
    try {
      const date = new Date().toISOString().split('T')[0].replace(/-/g, ''); // YYYYMMDD
      
      // Try to use crypto.randomUUID (requires secure context/HTTPS)
      if (typeof crypto !== 'undefined' && crypto.randomUUID) {
        const uuid = crypto.randomUUID().split('-')[0]; // First part of UUID
        return `errId_${date}_${uuid}`;
      }
      
      // Fallback: use timestamp + random string for HTTP/insecure contexts
      const random = Math.random().toString(36).substring(2, 10);
      return `errId_${date}_${random}`;
    } catch (err) {
      // Ultimate fallback if everything fails
      return `errId_${Date.now()}_${Math.random().toString(36).substring(2, 10)}`;
    }
  }

  /**
   * Track an error event
   * @param message Error message
   * @param stack Optional stack trace
   * @returns errorId - Unique identifier for this error
   */
  public trackError(message: string, stack?: string): string {
    // Generate unique error ID
    const errorId = this.generateErrorId();
    
    if (!this.config.enabled) return errorId;

    const event: MonitoringEvent = {
      eventType: MONITORING_EVENT_TYPES.ERROR,
      game: this.config.pageName,
      environment: this.environment,
      timestamp: Date.now(),
      errorInfo: {
        message,
        stack,
        errorId,
      },
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      sessionId: this.sessionId,
    };

    this.queueEvent(event);
    
    return errorId;
  }

  /**
   * Track a performance metric
   * @param latencyMs Latency in milliseconds
   */
  public trackPerformance(latencyMs: number): void {
    if (!this.config.enabled) return;

    const event: MonitoringEvent = {
      eventType: MONITORING_EVENT_TYPES.PERFORMANCE,
      game: this.config.pageName,
      environment: this.environment,
      timestamp: Date.now(),
      pageLoadLatency: latencyMs,
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      sessionId: this.sessionId,
    };

    this.queueEvent(event);
  }

  /**
   * Track a page view
   */
  public trackPageView(): void {
    if (!this.config.enabled) return;

    const event: MonitoringEvent = {
      eventType: MONITORING_EVENT_TYPES.PAGEVIEW,
      game: this.config.pageName,
      environment: this.environment,
      timestamp: Date.now(),
      url: typeof window !== 'undefined' ? window.location.href : undefined,
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : undefined,
      sessionId: this.sessionId,
    };

    this.queueEvent(event);
  }

  /**
   * Add event to queue and trigger flush if needed
   * Batching improves network efficiency and reduces API calls
   */
  private queueEvent(event: MonitoringEvent): void {
    this.eventQueue.push(event);

    // Flush immediately if batch is full
    if (this.eventQueue.length >= this.config.batchSize) {
      logger.log(`[Monitoring] Batch full (${this.eventQueue.length} events), flushing`);
      this.flush();
    } else {
      // Otherwise schedule a flush
      this.scheduleFlush();
    }
  }

  /**
   * Schedule a delayed flush if one isn't already scheduled
   */
  private scheduleFlush(): void {
    if (this.flushTimer !== null) return;

    this.flushTimer = window.setTimeout(() => {
      logger.debug(`[Monitoring] Flush interval reached, sending ${this.eventQueue.length} events`);

      this.flush();
    }, this.config.flushInterval);
  }

  /**
   * Send queued events to API endpoint
   * @param useBeacon Use navigator.sendBeacon for page unload (more reliable)
   */
  private async flush(useBeacon = false): Promise<void> {
    // Clear scheduled flush
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }

    if (this.eventQueue.length === 0) return;

    // Copy and clear queue
    const events = [...this.eventQueue];
    this.eventQueue = [];

    const payload = JSON.stringify({ events });

    logger.debug(`[Monitoring] Sending ${events.length} event(s) to ${this.apiEndpoint}`);
    logger.debug(`[Monitoring] Payload: ${payload}`);

    try {
      if (useBeacon && typeof navigator !== 'undefined' && navigator.sendBeacon) {
        // Use sendBeacon for page unload - more reliable as it's async and non-blocking
        const blob = new Blob([payload], { type: 'application/json' });
        const success = navigator.sendBeacon(this.apiEndpoint, blob);
        logger.debug(`[Monitoring] Beacon send ${success ? 'succeeded' : 'failed'}`);
      } else {
        // Use fetch for normal operation with keepalive to survive page transitions
        const response = await fetch(this.apiEndpoint, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: payload,
          keepalive: true, // Allows request to complete even if page unloads
        });
        
        if (response.ok) {
          logger.debug(`[Monitoring] Successfully sent ${events.length} event(s)`);
        } else {
          // Log errors in debug mode only to avoid console noise in development
          logger.debug(`[Monitoring] Server returned ${response.status}: ${response.statusText}`);
        }
      }
    } catch (error) {
      // Log errors in debug mode only to avoid console noise in development
      logger.debug('[Monitoring] Failed to send monitoring data:', error);
      // Don't re-queue events to avoid infinite loops and memory leaks
    }
  }

  /**
   * Clean up monitoring client
   * Flushes any remaining events and clears timers
   */
  public destroy(): void {
    logger.log('[Monitoring] Destroying monitoring client');
    this.flush();
    if (this.flushTimer !== null) {
      clearTimeout(this.flushTimer);
      this.flushTimer = null;
    }
  }
}

// ============================================
// Singleton Instance and Helper Functions
// ============================================
// These provide convenient access to monitoring without managing instance lifecycle

let monitoringInstance: SLAMonitoringClient | null = null;

/**
 * Initialize monitoring (singleton pattern)
 * If monitoring is already initialized, it will be destroyed and recreated
 * 
 * @param config Monitoring configuration
 * @returns SLAMonitoringClient instance
 */
export function initSLAMonitoring(config: MonitoringConfig): SLAMonitoringClient {
  if (monitoringInstance) {
    monitoringInstance.destroy();
  }
  monitoringInstance = new SLAMonitoringClient(config);
  return monitoringInstance;
}

/**
 * Get the current monitoring instance
 * @returns SLAMonitoringClient instance or null if not initialized
 */
export function getMonitoring(): SLAMonitoringClient | null {
  return monitoringInstance;
}

/**
 * Track an error (convenience function)
 * @param message Error message
 * @param stack Optional stack trace
 * @returns errorId - Unique identifier for this error
 */
export function trackError(message: string, stack?: string): string {
  return monitoringInstance?.trackError(message, stack) || '';
}

/**
 * Track a performance metric (convenience function)
 * @param latencyMs Latency in milliseconds
 */
export function trackPerformance(latencyMs: number): void {
  monitoringInstance?.trackPerformance(latencyMs);
}

/**
 * Track a page view (convenience function)
 */
export function trackPageView(): void {
  monitoringInstance?.trackPageView();
}
